/*
WCFBDNS-126

Manages the VAT-relief self-certification checkbox on the PDP. When checked, swaps the
add-to-cart form's submitted variant to the linked tax-exempt child variant, enables the
`_tax-exemption-declaration` line item property, and overlays the child variant's
(VAT-removed) price — both in the price display and in the Add to Cart button's own label
(e.g. "Add to Bag - £54.17"). The declaration persists across variant changes — re-applied
against whichever variant the shopper picks next — and only clears itself if the newly
selected variant has no linked tax-exempt child.
*/

import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {object} TaxExemptToggleRefs
 * @property {HTMLInputElement} checkbox - The self-certification checkbox.
 * @property {HTMLInputElement} declarationInput - The hidden line item property input.
 * @extends Component<TaxExemptToggleRefs>
 */
class TaxExemptToggleComponent extends Component {
  requiredRefs = ['checkbox', 'declarationInput'];
  #abortController = new AbortController();

  /** @type {string | null} */
  #originalVariantId = null;

  /** @type {string | null} */
  #originalPriceHTML = null;

  /** @type {string | null} */
  #originalCompareHTML = null;

  /** @type {string} */
  #originalCompareDisplay = '';

  /** @type {string | null} */
  #originalAddToCartText = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Deliberately re-parsed from the DOM on every access, never cached in a field.
   *
   * Combined Listings swap to an entirely different underlying product via a full `<main>`
   * morph (variant-picker.js's `updateMain`), which never dispatches `variant:update` at all
   * — the event `#onVariantUpdate` below relies on. `morph()` does correctly patch this
   * element's `data-variant-map` attribute to the new product's values (via `copyAttributes`
   * on the retained live node), so reading it fresh here is reliable. The lifecycle hook
   * meant to notice a re-render, `updatedCallback()`, is NOT a safe place to cache this
   * instead: `morph.js`'s `onAfterUpdate` calls it on the newly-*parsed*, never-inserted
   * response node (see `walk()` — it always *returns* the retained old node, but invokes the
   * hook with the discarded new one), not the real element left in the page. A fix that
   * relied on `updatedCallback()` to refresh a cached copy was shipped and confirmed NOT to
   * work for exactly this reason — it ran, just on a disconnected ghost nobody could see.
   * @returns {Record<string, {variantId: string|number, price: string, parentPrice: string, comparePrice: string}>}
   */
  get #variantMap() {
    try {
      return JSON.parse(this.dataset.variantMap ?? '{}');
    } catch (error) {
      console.error('tax-exempt-toggle: invalid variant map', error);
      return {};
    }
  }

  get #scope() {
    return this.closest('.product-details');
  }

  get #form() {
    return this.#scope?.querySelector('form[data-type="add-to-cart-form"]');
  }

  get #priceElement() {
    return this.#scope?.querySelector('product-price .price');
  }

  get #comparePriceElement() {
    return this.#scope?.querySelector('product-price .compare-at-price');
  }

  get #addToCartTextElement() {
    return this.#scope?.querySelector('[ref="addToCartButton"] .add-to-cart-text > span');
  }

  /**
   * Handles the self-certification checkbox being toggled.
   */
  handleToggle() {
    const form = this.#form;
    const variantIdInput = form?.querySelector('[ref="variantId"]');
    if (!form || !variantIdInput) return;

    // declarationInput lives in this component's own markup, not inside the add-to-cart
    // <form> itself (they're sibling blocks) — without this, FormData(form) silently drops
    // it on submit. The HTML `form` attribute associates an input with a form it isn't a
    // descendant of; set it here (idempotent) rather than in connectedCallback, since custom
    // elements upgrade in document order and the <form> may not exist yet at that point.
    //
    // Deliberately `form.getAttribute('id')`, not `form.id`: this form contains a control
    // named "id" (the variant input), and HTMLFormElement's named-control access quirk
    // ([OverrideBuiltins]) makes `form.id` resolve to THAT ELEMENT instead of the form's own
    // id attribute whenever a same-named control exists — getAttribute is unaffected by it.
    const formId = form.getAttribute('id');
    if (formId) this.refs.declarationInput.setAttribute('form', formId);

    if (this.refs.checkbox.checked) {
      const mapping = this.#variantMap[this.dataset.parentVariantId ?? ''];

      if (!mapping) {
        this.refs.checkbox.checked = false;
        return;
      }

      this.#applyOverride(mapping, variantIdInput);
    } else {
      this.#restoreParentState(variantIdInput);
    }
  }

  /**
   * Overrides the form's variant, the displayed price, and the Add to Cart button's own
   * price text with the tax-exempt child's. Snapshots whatever is currently in place first,
   * so it can be restored later.
   *
   * @param {{variantId: string|number, price: string, parentPrice: string, comparePrice: string}} mapping
   * @param {HTMLInputElement} variantIdInput
   */
  #applyOverride(mapping, variantIdInput) {
    this.#originalVariantId = variantIdInput.value;
    variantIdInput.value = String(mapping.variantId);
    this.refs.declarationInput.disabled = false;

    const priceElement = this.#priceElement;
    if (priceElement) {
      this.#originalPriceHTML = priceElement.innerHTML;
      priceElement.textContent = mapping.price;
    }

    const compareElement = this.#comparePriceElement;
    if (compareElement) {
      this.#originalCompareHTML = compareElement.innerHTML;
      this.#originalCompareDisplay = compareElement.style.display;

      if (mapping.comparePrice) {
        compareElement.textContent = mapping.comparePrice;
        compareElement.style.display = '';
      } else {
        // The tax-exempt child isn't on sale even though the parent variant is —
        // nothing accurate to show here, so hide the "was £X" rather than leave it stale.
        compareElement.style.display = 'none';
      }
    }

    const addToCartTextElement = this.#addToCartTextElement;
    if (addToCartTextElement) {
      this.#originalAddToCartText = addToCartTextElement.textContent;
      if (this.#originalAddToCartText?.includes(mapping.parentPrice)) {
        addToCartTextElement.textContent = this.#originalAddToCartText.replace(mapping.parentPrice, mapping.price);
      }
    }
  }

  /**
   * @param {HTMLInputElement} variantIdInput
   */
  #restoreParentState(variantIdInput) {
    if (this.#originalVariantId !== null) {
      variantIdInput.value = this.#originalVariantId;
      this.#originalVariantId = null;
    }

    this.refs.declarationInput.disabled = true;

    const priceElement = this.#priceElement;
    if (priceElement && this.#originalPriceHTML !== null) {
      priceElement.innerHTML = this.#originalPriceHTML;
    }
    this.#originalPriceHTML = null;

    const compareElement = this.#comparePriceElement;
    if (compareElement && this.#originalCompareHTML !== null) {
      compareElement.innerHTML = this.#originalCompareHTML;
      compareElement.style.display = this.#originalCompareDisplay;
    }
    this.#originalCompareHTML = null;
    this.#originalCompareDisplay = '';

    const addToCartTextElement = this.#addToCartTextElement;
    if (addToCartTextElement && this.#originalAddToCartText !== null) {
      addToCartTextElement.textContent = this.#originalAddToCartText;
    }
    this.#originalAddToCartText = null;
  }

  /**
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    this.dataset.parentVariantId = event.detail.resource?.id ?? '';

    if (!this.refs.checkbox.checked) return;

    const variantIdInput = this.#form?.querySelector('[ref="variantId"]');
    if (!variantIdInput) return;

    const mapping = this.#variantMap[this.dataset.parentVariantId];

    if (!mapping) {
      // The newly selected variant has no linked tax-exempt child — can't honor the
      // declaration here. ProductFormComponent has already pointed variantId at this new
      // parent variant, variant-picker.js has already morphed the price to match, and the
      // add-to-cart button has already been morphed in with its own new label — all ahead
      // of this event — so there's nothing of ours left to restore, just drop state.
      this.refs.checkbox.checked = false;
      this.#originalVariantId = null;
      this.#originalPriceHTML = null;
      this.#originalCompareHTML = null;
      this.#originalCompareDisplay = '';
      this.#originalAddToCartText = null;
      this.refs.declarationInput.disabled = true;
      return;
    }

    // Snapshot the new parent variant/price/button label (already in place by now, see
    // above) as the "original" to restore to later, then re-apply the override on top of it.
    this.#applyOverride(mapping, variantIdInput);
  };
}

if (!customElements.get('tax-exempt-toggle')) {
  customElements.define('tax-exempt-toggle', TaxExemptToggleComponent);
}
